<template>
  <section class="section library-section" id="library">
    <div class="section-copy">
      <h2 class="section-title">{{ $t('home.library.title') }}</h2>
      <p>{{ $t('home.library.subtitle') }}</p>
    </div>

    <div class="library-feature">
      <div class="library-feature-copy">
        <span class="library-media">
          <span class="material-icons-outlined" aria-hidden="true">play_circle</span>
          {{ $t('home.library.vlogMedia') }}
        </span>
        <h3>{{ $t('home.library.vlogTitle') }}</h3>
        <p>{{ $t('home.library.vlogDescription') }}</p>
        <ul>
          <li v-for="point in $tm('home.library.vlogPoints')" :key="point">{{ point }}</li>
        </ul>
        <div class="action-buttons">
          <button type="button" class="btn btn-primary" @click="openCategory('youtube-vlog')">
            {{ $t('home.library.vlogAction') }}
            <span class="material-icons" aria-hidden="true">arrow_forward</span>
          </button>
        </div>
      </div>

      <button
        type="button"
        class="library-shot"
        :aria-label="$t('home.library.vlogAction')"
        @click="openCategory('youtube-vlog')"
      >
        <img
          :src="vlogShotSrc"
          width="1180"
          height="656"
          :alt="$t('home.library.vlogShotAlt')"
          loading="lazy"
        >
      </button>
    </div>

    <div class="library-more">
      <div class="library-more-head">
        <h3>{{ $t('home.library.moreTitle') }}</h3>
        <button type="button" class="library-browse" @click="openLibrary">
          {{ $t('home.library.browseAll') }}
          <span class="material-icons" aria-hidden="true">arrow_forward</span>
        </button>
      </div>
      <ul class="library-grid">
        <li v-for="category in moreCategories" :key="category.id">
          <button type="button" class="library-card" @click="openCategory(category.id)">
            <span class="library-card-image">
              <img :src="category.image" :alt="category.title" loading="lazy">
            </span>
            <span class="library-card-body">
              <span class="library-card-media">
                {{ category.id.startsWith('youtube-') ? $t('home.library.video') : $t('home.library.audio') }}
              </span>
              <strong>{{ category.title }}</strong>
              <span class="library-card-desc">{{ category.description }}</span>
            </span>
          </button>
        </li>
      </ul>
    </div>
  </section>
</template>

<script>
import { useViews } from '@/composables/views';
import { getFallbackCategories } from '@/assets/material/materialCatalog';
import vlogShotZh from '@/assets/home/library-vlog-zh.webp';
import vlogShotEn from '@/assets/home/library-vlog-en.webp';

const MORE_CATEGORY_IDS = ['youtube-real-life', 'youtube-beginner', 'ielts', 'podcasts', 'teded', 'bbc6minute'];

export default {
  name: 'HomeLibrarySection',
  setup() {
    const { handleViewChange } = useViews();
    return { handleViewChange };
  },
  computed: {
    categories() {
      return getFallbackCategories((key) => this.$t(key));
    },
    moreCategories() {
      return MORE_CATEGORY_IDS.map((id) => this.categories.find((category) => category.id === id));
    },
    vlogShotSrc() {
      return this.$i18n.locale === 'en' ? vlogShotEn : vlogShotZh;
    }
  },
  methods: {
    openCategory(categoryId) {
      this.handleViewChange('Material', { categoryId });
    },
    openLibrary() {
      this.handleViewChange('Material');
    }
  }
};
</script>

<style scoped>
.library-feature {
  display: grid;
  grid-template-columns: minmax(280px, 0.8fr) minmax(0, 1.6fr);
  align-items: center;
  gap: clamp(var(--space-8), 4vw, var(--space-16));
  width: min(1180px, 100%);
}

.library-media,
.library-card-media {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  color: var(--signal-active-strong);
  font-family: var(--font-family-data);
  font-size: var(--font-size-xs);
  font-weight: 500;
  letter-spacing: 0.02em;
}

.library-media .material-icons-outlined {
  font-size: 1rem;
}

.library-feature-copy h3 {
  margin: var(--space-3) 0 0;
  color: var(--ink-primary);
  font-size: clamp(1.75rem, 2.4vw, 2.25rem);
  font-weight: 700;
  line-height: 1.15;
  letter-spacing: -0.02em;
}

.library-feature-copy p {
  margin: var(--space-4) 0 0;
  color: var(--ink-secondary);
  font-size: 1rem;
  line-height: 1.65;
}

.library-feature-copy ul {
  display: grid;
  gap: var(--space-3);
  margin: var(--space-6) 0 0;
  padding: var(--space-5) 0 0;
  border-top: 1px solid var(--border-subtle);
  list-style: none;
}

.library-feature-copy li {
  position: relative;
  padding-left: var(--space-5);
  color: var(--ink-primary);
  font-size: 0.9375rem;
  line-height: 1.55;
}

.library-feature-copy li::before {
  content: '';
  position: absolute;
  top: 0.62em;
  left: 0;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--ink-tertiary);
}

.library-feature-copy .action-buttons {
  margin-top: var(--space-8);
}

.library-feature-copy .btn .material-icons {
  font-size: 1.1rem;
}

.library-shot {
  display: block;
  width: 100%;
  padding: 0;
  overflow: hidden;
  border: 1px solid var(--border-default);
  border-radius: 14px;
  background: var(--surface-panel);
  box-shadow: var(--shadow-float);
  cursor: pointer;
  transition: transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease;
}

.library-shot img {
  display: block;
  width: 100%;
  height: auto;
}

.library-shot:hover {
  transform: translateY(-2px);
  border-color: var(--border-active);
  box-shadow: var(--shadow-lift);
}

.library-shot:focus-visible,
.library-card:focus-visible,
.library-browse:focus-visible {
  outline: none;
  box-shadow: var(--focus-ring-brand-strong);
}

.library-more {
  width: min(1180px, 100%);
}

.library-more-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--space-4);
  margin-bottom: var(--space-5);
}

.library-more-head h3 {
  margin: 0;
  color: var(--ink-primary);
  font-size: 1.25rem;
  font-weight: 600;
}

.library-browse {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  padding: var(--space-1) 0;
  border: 0;
  border-radius: var(--radius-sm);
  background: none;
  color: var(--signal-active-strong);
  font: inherit;
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
}

.library-browse .material-icons {
  font-size: 1rem;
  transition: transform 180ms ease;
}

.library-browse:hover .material-icons {
  transform: translateX(2px);
}

.library-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--space-4);
  margin: 0;
  padding: 0;
  list-style: none;
}

.library-card {
  display: flex;
  align-items: stretch;
  gap: var(--space-4);
  width: 100%;
  height: 100%;
  padding: var(--space-3);
  border: 1px solid var(--border-subtle);
  border-radius: 10px;
  background: var(--surface-panel);
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition: border-color 180ms ease, box-shadow 180ms ease;
}

.library-card:hover {
  border-color: var(--border-default);
  box-shadow: var(--shadow-hover);
}

.library-card-image {
  flex: 0 0 96px;
  overflow: hidden;
  border-radius: 6px;
  background: var(--surface-subtle);
}

.library-card-image img {
  display: block;
  width: 100%;
  height: 100%;
  min-height: 72px;
  object-fit: cover;
}

.library-card-body {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  min-width: 0;
  padding: var(--space-1) 0;
}

.library-card-body strong {
  color: var(--ink-primary);
  font-size: 0.9375rem;
  font-weight: 600;
  line-height: 1.35;
}

.library-card-desc {
  display: -webkit-box;
  overflow: hidden;
  color: var(--ink-secondary);
  font-size: 0.875rem;
  line-height: 1.5;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

@media (prefers-reduced-motion: reduce) {
  .library-shot,
  .library-browse .material-icons {
    transition: none;
  }

  .library-shot:hover,
  .library-browse:hover .material-icons {
    transform: none;
  }
}

@media (max-width: 1024px) {
  .library-feature {
    grid-template-columns: 1fr;
  }

  .library-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 640px) {
  .library-grid {
    grid-template-columns: 1fr;
  }

  .library-more-head {
    flex-direction: column;
    align-items: flex-start;
    gap: var(--space-2);
  }
}
</style>
